/**
 * Every duration and easing in the reward-unlock sequence, in one place (Gap 15, strategy §4).
 *
 * The point of the module is auditability: "does the code match the spec" should be a two-file diff
 * rather than archaeology across SCSS and callbacks. So the names mirror the spec's own beats, and a
 * beat the spec states as an absolute time is transcribed as one — *unless* it is the sum of beats
 * already here, in which case it is computed from them and lives under "Derived beats". A retune then
 * carries its dependants with it instead of leaving them stale.
 *
 * **Source of truth: the "Sequence of Events" image embedded in the JIRA task** (ruling 2026-08-05).
 * It agrees with the board's written sequence (text node `16080:28706`) and with the Figma Make
 * prototype throughout. It was named the tiebreaker because the original artifact ruling ranks board
 * over prototype and so could not settle a board-*versus*-board disagreement: the tracker's own spec
 * artboard `15795:49732` annotates a 200ms delay and a 220ms fill, which turned out to describe the
 * tracker's resting in-page rail update — the `transition: width 0.3s ease` shipping today at
 * `RewardsTracker.vue:216` — and not this sequence at all.
 *
 * **Two values are deliberately absent, because they are visual and already live in SCSS.** Putting
 * them here too would create two sources for one value:
 *
 * - The node glow, `0 0 12px 0 #bb4a9266` (`RewardsTracker.vue:215` and `:275`). The spec says 10px;
 *   we keep the file's `Pink Drop Shadow` variable at 12px as a recorded deviation — same colour,
 *   since `rgba(187,74,146,0.4)` *is* `#bb4a9266`. See the plan, A1.
 * - The page dim, 25% (`AppCartSidebarPanel.vue`'s `.cart-sidebar-scrim`). The spec says 40%; the
 *   board node `15980:29322` wins on visual values. See the plan, S6.
 *
 * **What A2a inherits.** `RewardsTracker`'s resting 300ms `width` transition will fight an 800ms
 * JS-driven fill. The machine has to own `width` for the length of the sequence and hand it back, or
 * suppress the resting transition while running.
 */

// --- Absolute beats, as the spec states them ---------------------------------------------------

/** Drawer slides in from the right; the page behind dims over the same window. */
export const DRAWER_IN_MS = 500

/** Fill bar begins animating toward the icon — cold open, i.e. the cart was closed. */
export const FILL_START_MS = 700

/** Fill start when the cart is already on screen. The whole tail shifts earlier with it. */
export const FILL_START_WARM_MS = 400

/** Icon swaps to a white checkmark with a scale pop; the glow intensifies and the label turns pink. */
export const CHECK_SWAP_MS = 1800

/** Reward card transitions — banner text, lock overlay out, unlocked copy in — all simultaneously. */
export const CELEBRATION_START_MS = 2000

/** Reward lands in the cart as a free line item, and the card advances to the next locked reward. */
export const REWARD_ROW_ARRIVAL_MS = 4000

// --- Durations ---------------------------------------------------------------------------------

/** Bar fill. Paired with {@link FILL_EASING}. */
export const FILL_DURATION_MS = 800

/** Into the fill, not from the start of the sequence — see {@link NODE_MAGENTA_MS}. */
export const NODE_MAGENTA_OFFSET_MS = 500

/** CSS transition on the node's magenta background and glow. */
export const NODE_MAGENTA_TRANSITION_MS = 400

/** Checkmark scale pop, `1 → 1.25 → 1`. */
export const CHECK_POP_MS = 350

/**
 * The 2,000ms card transition — the **incoming** half. Every element crossfades together, but the
 * spec makes the two directions asymmetric: "cross-fade, 300 ms in / 200 ms out, with a 12 px rise on
 * enter and an 8 px lift on exit (350 ms)". Content enters from below and leaves upward, so the
 * celebration reads as the new state pushing the old one out of the way rather than a plain dissolve.
 */
export const CELEBRATION_FADE_MS = 300

/** The outgoing half, which is quicker — the old state should be gone before the new one lands. */
export const CELEBRATION_FADE_OUT_MS = 200

/** Incoming content starts this far below its resting position and rises into it. */
export const CELEBRATION_ENTER_RISE_PX = 12

/** Outgoing content lifts this far as it leaves. Negative: upward. */
export const CELEBRATION_EXIT_LIFT_PX = -8

/**
 * The exit lift's own duration, which the spec gives separately from the 200ms fade — so outgoing
 * content finishes fading before it finishes moving, and the movement is what the eye follows out.
 */
export const CELEBRATION_EXIT_LIFT_MS = 350

/**
 * The longest property in the crossfade, and therefore how long the whole transition takes to land.
 * `settle()` waits this out rather than the fade alone, or collapsing mid-sequence would cut the
 * movement off partway.
 */
export const CELEBRATION_CROSSFADE_MS = Math.max(
  CELEBRATION_FADE_MS,
  CELEBRATION_FADE_OUT_MS,
  CELEBRATION_EXIT_LIFT_MS
)

/**
 * Crossfade as the reward card advances to the next locked reward, at {@link REWARD_ROW_ARRIVAL_MS}.
 *
 * Shares a duration with {@link CHECK_POP_MS} and is **not** the same beat. They are named separately
 * so that tuning one cannot silently move the other — the guide's timings table originally carried
 * only the checkmark pop, and the two would have been collapsed onto it.
 */
export const REWARD_CARD_CROSSFADE_MS = 350

/**
 * How long a run holds on its final state before the drawer leaves — **shared by both endings.**
 *
 * The spec states this only for the celebration, as a hold on the next locked reward card, and the
 * close was transcribed as an absolute 6,000 rather than as that beat plus this dwell. Which is the
 * only reason the two endings could disagree: a non-reward run has no 4,000ms beat, so it inherited
 * the same 6,000 with nothing behind it and sat on a finished bar for 4.5 seconds. The business caught
 * exactly that in the sandbox (2026-08-12) and asked for "around 2.0 secs" on both — which is this
 * number, so the fix is to *consume* it on both paths rather than to retune anything.
 *
 * Named for the ending rather than the celebration now that both closes derive from it. It was
 * `CELEBRATION_DWELL_MS` and had no references at all, which is the clue that the ending was
 * hardcoded past it.
 */
export const SEQUENCE_END_DWELL_MS = 2000

// --- Derived beats -----------------------------------------------------------------------------
//
// The spec names these as absolute times too. They are computed rather than transcribed so they
// cannot drift away from the parts they are made of — if someone retunes the fill, these follow.

/** Fill bar completes. The spec's 1,500ms beat. */
export const FILL_END_MS = FILL_START_MS + FILL_DURATION_MS

/** Icons turn magenta, mid-fill. The spec's 1,200ms beat, annotated "500ms into the fill". */
export const NODE_MAGENTA_MS = FILL_START_MS + NODE_MAGENTA_OFFSET_MS

/**
 * Drawer slides back out on a run that celebrated. The spec's 6,000ms beat.
 *
 * Derived rather than transcribed, which is the whole point: the ending is "the run's last beat plus
 * {@link SEQUENCE_END_DWELL_MS}", and writing 6,000 as a literal hid that rule well enough that the
 * second ending below could never have been noticed as missing.
 */
export const AUTO_CLOSE_MS = REWARD_ROW_ARRIVAL_MS + SEQUENCE_END_DWELL_MS

/**
 * Drawer slides back out on a run that unlocked nothing.
 *
 * **No artifact specifies this, and none is in conflict.** The "Sequence of Events" image describes the
 * *unlock* sequence; a mutation that crosses no threshold has no 4,000ms beat to hold on, and its
 * behaviour — open, fill, close — comes from the business decisions of 2026-08-06/07 rather than from
 * the spec. So this is unspecified ground the business has now filled in, not a deviation from a ruling.
 *
 * The fill is the last thing that moves on this path, so the dwell hangs off {@link FILL_END_MS}. The
 * warm-open shift composes for free, because the fill end and the close move earlier together.
 */
export const AUTO_CLOSE_NON_REWARD_MS = FILL_END_MS + SEQUENCE_END_DWELL_MS

/**
 * How much earlier every beat runs on a warm open. The spec describes the warm sequence as identical
 * but shifted, so one offset expresses it rather than a second table of constants.
 */
export const WARM_OPEN_SHIFT_MS = FILL_START_MS - FILL_START_WARM_MS

// --- Easing and geometry -----------------------------------------------------------------------

/** Progress-bar fill. A plain CSS transition in the prototype, not a spring. */
export const FILL_EASING = 'cubic-bezier(0.4, 0, 0.6, 1)'

/**
 * Drawer slide. The spec calls for `spring(stiffness: 290, damping: 28)`, mass 1 — ζ ≈ 0.82, so ~1%
 * overshoot settling around 330ms. A cubic-bezier reproduces that within perceptual tolerance, which
 * is why this feature needs no physics library. The spring's numbers are kept below as provenance.
 */
export const DRAWER_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)'

/** The spring the drawer easing approximates. Recorded so the approximation stays checkable. */
export const DRAWER_SPRING = { stiffness: 290, damping: 28, mass: 1 } as const

/** Fill bar extends beyond the outer nodes by this much on each side. */
export const BAR_OVERHANG_PX = 40
